import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { arClass } from "../../../../utils/useArClass";
import Txt from "../../../Text";
import CustomSelect from "../../../CustomSelect";
import api from "../../../../api";
import { showAlert } from "../../../../redux/alert/alertActions";
import textTranslator from "../../../../utils/useTextTranslator";
import { ArrowLeft, Save, Loader, Brain, Flag, File, Code, Plus, X, CircleQuestionMark, Package } from "lucide-react";

const Text = ({ children, className, page = "MY_REQUESTS" }) => {
  return (
    <Txt page={page} className={className} keyName={page}>
      {children}
    </Txt>
  );
};

const priorityTypes = {
  LOW: {
    label: {
      en: "Low",
      ar: "منخفض",
      fr: "Faible",
    },
    value: "LOW",
    color: "green",
  },
  MEDIUM: {
    label: {
      en: "Medium",
      ar: "متوسط",
      fr: "Moyen",
    },
    value: "MEDIUM",
    color: "yellow",
  },
  HIGH: {
    label: {
      en: "High",
      ar: "مرتفع",
      fr: "Élevé",
    },
    value: "HIGH",
    color: "red",
  },
  CRITICAL: {
    label: {
      en: "Critical",
      ar: "حرج",
      fr: "Critique",
    },
    value: "CRITICAL",
    color: "gray",
  },
};

const CreateModelRequest = ({ onCancel }) => {
  const darkMode = useSelector((state) => state.settings.darkMode);
  const defaultLanguage = useSelector(
    (state) => state.settings.defaultLanguage
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    priority: "MEDIUM",
    modelName: "",
    modelType: "",
    useCases: "",
  });

  const [modelFields, setModelFields] = useState([]);
  const [relationships, setRelationships] = useState([]);
  const [mockData, setMockData] = useState([]);

  const [fieldInput, setFieldInput] = useState({ name: "", type: "" });
  const [relationshipInput, setRelationshipInput] = useState("");
  const [mockDataInput, setMockDataInput] = useState({ key: "", value: "" });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Model Fields handlers
  const addModelField = () => {
    if (fieldInput.name.trim()) {
      setModelFields([...modelFields, { ...fieldInput, id: Date.now() }]);
      setFieldInput({ name: "", type: "" });
    }
  };

  const removeModelField = (id) => {
    setModelFields(modelFields.filter((field) => field.id !== id));
  };

  // Relationships handlers - simplified for users
  const addRelationship = () => {
    if (relationshipInput.trim()) {
      setRelationships([...relationships, { value: relationshipInput, id: Date.now() }]);
      setRelationshipInput("");
    }
  };

  const removeRelationship = (id) => {
    setRelationships(relationships.filter((rel) => rel.id !== id));
  };

  // Mock Data handlers
  const addMockData = () => {
    if (mockDataInput.key.trim()) {
      setMockData([...mockData, { ...mockDataInput, id: Date.now() }]);
      setMockDataInput({ key: "", value: "" });
    }
  };

  const removeMockData = (id) => {
    setMockData(mockData.filter((item) => item.id !== id));
  };

  const validateJSON = (str, fieldName) => {
    try {
      JSON.parse(str);
      return true;
    } catch (error) {
      dispatch(
        showAlert({
          type: "error",
          title: "Error",
          message: `Invalid JSON in ${fieldName}`,
        })
      );
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Convert arrays to JSON format
      const modelFieldsData = modelFields.map(({ id, ...field }) => field);
      const relationshipsData = {};
      relationships.forEach(({ id, value }, index) => {
        relationshipsData[`relationship_${index + 1}`] = value;
      });
      const mockDataData = {};
      mockData.forEach(({ id, key, value }) => {
        mockDataData[key] = value;
      });

      const response = await api.post("it-requests", {
        title: formData.title,
        description: formData.description,
        priority: formData.priority,
        requestType: "MODEL_REQUEST",
        modelDetails: {
          modelName: formData.modelName,
          modelType: formData.modelType,
          modelFields: modelFieldsData,
          relationships: relationshipsData,
          useCases: formData.useCases,
          mockData: mockDataData,
        },
      });

      dispatch(
        showAlert({
          type: "success",
          title: textTranslator("Success", "MY_REQUESTS"),
          message: textTranslator("Model request created successfully", "MY_REQUESTS"),
        })
      );

      navigate("/my-requests");
    } catch (error) {
      console.error("Error creating request:", error);
      dispatch(
        showAlert({
          type: "error",
          title: "Error",
          message: error?.response?.data?.message || "Failed to create request",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={arClass(`min-h-screen ${
        darkMode ? "bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" : "bg-gradient-to-br from-gray-50 via-blue-50 to-gray-50"
      } transition-colors duration-300 py-8`)}>
      <div className={arClass(" mx-auto px-4 sm:px-6 lg:px-8")}>
        {/* Back Button */}
        <motion.button
          onClick={onCancel}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className={arClass(
            `flex items-center gap-2 px-4 py-2 rounded-lg font-medium mb-8 transition-all ${
              darkMode
                ? "bg-gray-800/50 hover:bg-gray-700/80 text-gray-300 hover:text-white border border-gray-700"
                : "bg-white/50 hover:bg-white text-gray-600 hover:text-gray-900 border border-gray-200 shadow-sm"
            }`
          )}
          whileHover={{ scale: 1.05, x: -5 }}
          whileTap={{ scale: 0.95 }}
        >
          <ArrowLeft className="w-4 h-4" />
          <Text>Back</Text>
        </motion.button>

        {/* Main Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className={arClass(
            `rounded-2xl overflow-hidden shadow-2xl border ${
              darkMode
                ? "bg-gradient-to-br from-gray-800 to-gray-900 border-gray-700/50"
                : "bg-white border-gray-200/50"
            }`
          )}
        >
          {/* Header Section */}
          <div className={arClass(`bg-gradient-to-r from-purple-600 to-pink-600 px-2 py-4 sm:p-6 relative overflow-hidden`)}>
            <motion.div
              className="absolute inset-0 opacity-10"
              animate={{
                background: [
                  "radial-gradient(circle at 20% 50%, #ffffff 0%, transparent 50%)",
                  "radial-gradient(circle at 80% 50%, #ffffff 0%, transparent 50%)",
                  "radial-gradient(circle at 20% 50%, #ffffff 0%, transparent 50%)",
                ],
              }}
              transition={{ duration: 4, repeat: Infinity }}
            />
            <div className={arClass("relative z-10")}>
              <div className={arClass("flex items-center gap-3 mb-2")}>
                <Package className="text-white w-10 h-10 max-sm:w-6 max-sm:h-6" />
                <h1 className="text-3xl max-sm:text-xl font-bold text-white">
                  <Text>Create Model Request</Text>
                </h1>
              </div>
              <p className={arClass("text-purple-100 text-sm")}>
                <Text>Request new ML models or data structures</Text>
              </p>
            </div>
          </div>

          {/* Content Section */}
          <div className={arClass(`px-2 py-4 sm:p-6`)}>
            {/* Form */}
            <form onSubmit={handleSubmit} className={arClass("normal-form space-y-6")}>
              {/* Section 1: Request Details */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className={arClass(
                  `px-2 py-4 sm:p-6 rounded-xl border-2 ${
                    darkMode
                      ? "bg-gray-800/50 border-gray-700/50"
                      : "bg-gray-50/50 border-gray-200"
                  }`
                )}
              >
                <div className={arClass("flex items-center gap-2 mb-6")}>
                  <File className={arClass("text-lg " + (darkMode ? "text-blue-400" : "text-blue-600"))} />
                  <h2 className={arClass(`text-lg font-bold ${darkMode ? "text-white" : "text-gray-900"}`)}>
                    <Text>Request Information</Text>
                  </h2>
                </div>

                <div className={arClass("space-y-4")}>
                  {/* Title */}
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.25 }}
                  >
                    <label className={arClass(`text-sm font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"}`)}>
                      <Text>Title</Text> <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      placeholder={textTranslator("titlePlaceholder", "MY_REQUESTS")}
                      required
                      className={arClass(
                        `w-full mt-4 px-4 py-3 rounded-lg border transition-all ${
                          darkMode
                            ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20"
                            : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20"
                        }`
                      )}
                    />
                  </motion.div>

                  {/* Description */}
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    <label className={arClass(`text-sm font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"}`)}>
                      <Text>Description</Text> <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder={textTranslator("descriptionPlaceholder", "MY_REQUESTS")}
                      required
                      rows={4}
                      className={arClass(
                        `w-full mt-4 px-4 py-3 rounded-lg border transition-all resize-none ${
                          darkMode
                            ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20"
                            : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20"
                        }`
                      )}
                    />
                  </motion.div>
                </div>
              </motion.div>

              {/* Section 2: Priority */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
                className={arClass(
                  `px-2 py-4 sm:p-6 rounded-xl border-2 ${
                    darkMode
                      ? "bg-gray-800/50 border-gray-700/50"
                      : "bg-gray-50/50 border-gray-200"
                  }`
                )}
              >
                <div className={arClass("flex items-center gap-2 mb-6")}>
                  <Flag className={arClass("text-lg " + (darkMode ? "text-yellow-400" : "text-yellow-600"))} />
                  <h2 className={arClass(`text-lg font-bold  ${darkMode ? "text-white" : "text-gray-900"}`)}>
                    <Text>Request Type & Priority</Text>
                  </h2>
                </div>

                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <label className={arClass(`text-sm font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"}`)}>
                    <Text>Priority</Text>
                  </label>
                  <CustomSelect
                    options={Object.entries(priorityTypes).map(
                      ([key, value]) => ({
                        value: key,
                        label: value.label[defaultLanguage] || key,
                      }),
                    )}
                    value={formData.priority || ""}
                    name="priority"
                    onChange={handleInputChange}
                    placeholder={textTranslator("priorityPlaceholder", "MY_REQUESTS")}
                    allowCreate={false}
                    allowSearch={false}
                    className="max-sm:text-sm mt-4"
                  />
                </motion.div>
              </motion.div>

              {/* Section 3: Model Details */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className={arClass(
                  `px-2 py-4 sm:p-6 rounded-xl border-2 ${
                    darkMode
                      ? "bg-gradient-to-r from-purple-900/20 to-pink-900/20 border-purple-600/50"
                      : "bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200"
                  }`
                )}
              >
                <div className={arClass("flex items-center gap-2 mb-6")}>
                  <Brain className={arClass("text-lg " + (darkMode ? "text-purple-400" : "text-purple-600"))} />
                  <h2 className={arClass(`text-lg font-bold  ${darkMode ? "text-white" : "text-gray-900"}`)}>
                    <Text>Model Details</Text>
                  </h2>
                </div>

                <div className={arClass("grid grid-cols-1 sm:grid-cols-2 gap-4")}>
                  {/* Model Name */}
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.55 }}
                  >
                    <label className={arClass(`text-sm font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"}`)}>
                      <Text>Model Name</Text> <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="modelName"
                      value={formData.modelName}
                      onChange={handleInputChange}
                      placeholder={textTranslator("modelNamePlaceholder", "MY_REQUESTS")}
                      required
                      className={arClass(
                        `w-full mt-4 px-4 py-3 rounded-lg border transition-all ${
                          darkMode
                            ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20"
                            : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20"
                        }`
                      )}
                    />
                  </motion.div>

                  {/* Model Type */}
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.6 }}
                  >
                    <label className={arClass(`text-sm font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"}`)}>
                      <Text>Model Type</Text>
                    </label>
                    <input
                      type="text"
                      name="modelType"
                      value={formData.modelType}
                      onChange={handleInputChange}
                      placeholder={textTranslator("modelTypePlaceholder", "MY_REQUESTS")}
                      className={arClass(
                        `w-full mt-4 px-4 py-3 rounded-lg border transition-all ${
                          darkMode
                            ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20"
                            : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20"
                        }`
                      )}
                    />
                  </motion.div>
                </div>
              </motion.div>

              {/* Section 4: Use Cases */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.65 }}
                className={arClass(
                  `px-2 py-4 sm:p-6 rounded-xl border-2 ${
                    darkMode
                      ? "bg-gray-800/50 border-gray-700/50"
                      : "bg-gray-50/50 border-gray-200"
                  }`
                )}
              >
                <label className={arClass(`text-sm font-semibold flex items-center gap-2 ${darkMode ? "text-gray-300" : "text-gray-700"}`)}>
                  <Brain className={arClass("text-base max-sm:text-sm " + (darkMode ? "text-gray-400" : "text-gray-600"))} />
                  <Text>Use Cases</Text>
                </label>
                <textarea
                  name="useCases"
                  value={formData.useCases}
                  onChange={handleInputChange}
                  placeholder={textTranslator("useCasesPlaceholder", "MY_REQUESTS")}
                  rows={3}
                  className={arClass(
                    `w-full mt-4 px-4 py-3 rounded-lg border transition-all resize-none ${
                      darkMode
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20"
                    }`
                  )}
                />
              </motion.div>

              {/* Section 5: JSON Data */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className={arClass(
                  `px-2 py-4 sm:p-6 rounded-xl border-2 ${
                    darkMode
                      ? "bg-gray-800/50 border-gray-700/50"
                      : "bg-gray-50/50 border-gray-200"
                  }`
                )}
              >
                <div className={arClass("flex items-center gap-2 mb-6")}>
                  <Code className={arClass("text-lg " + (darkMode ? "text-green-400" : "text-green-600"))} />
                  <h2 className={arClass(`text-lg font-bold  ${darkMode ? "text-white" : "text-gray-900"}`)}>
                    <Text>Technical Details</Text>
                  </h2>
                </div>

                {/* Help Text */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.72 }}
                  className={arClass(
                    `mb-6 px-2 py-4 sm:p-4 rounded-lg border ${
                      darkMode
                        ? "bg-blue-900/20 border-blue-600/30 text-blue-200"
                        : "bg-blue-50 border-blue-200 text-blue-800"
                    }`
                  )}
                >
                  <p className={arClass("text-sm font-medium mb-2 flex items-center gap-1 ")}>
                    <CircleQuestionMark/> <Text>How to fill Technical Details</Text>:
                  </p>
                  <ul className={arClass("text-xs space-y-1 ml-4")}>
                    <li>• <strong><Text>Model Fields</Text></strong>: <Text>List the data fields your model will work with (e.g., name, age, email)</Text></li>
                    <li>• <strong><Text>Relationships</Text></strong>: <Text>Link to other entities/tables in your system</Text></li>
                    <li>• <strong><Text>Mock Data</Text></strong>: <Text>Provide example values to test the model</Text></li>
                  </ul>
                </motion.div>

                <div className={arClass("space-y-6")}>
                  {/* Model Fields */}
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.75 }}
                  >
                    <label className={arClass(`text-sm font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"}`)}>
                      <Text>Model Fields</Text>
                    </label>
                    <p className={arClass(`text-xs mt-1 ${darkMode ? "text-gray-400" : "text-gray-600"}`)}>
                      <Text>List the data fields your model will work with (e.g., name, age, email)</Text>
                    </p>
                    <div className={arClass("mt-4 space-y-3")}>
                      {/* Input Fields */}
                      <div className={arClass("flex gap-2 ")}>
                        <div className={arClass("flex gap-2 max-sm:flex-col w-full")}>
                          <input
                          type="text"
                          value={fieldInput.name}
                          onChange={(e) => setFieldInput({ ...fieldInput, name: e.target.value })}
                          placeholder={textTranslator("fieldNamePlaceholder", "MY_REQUESTS")}
                          className={arClass(
                            `flex-1 px-4 py-2 rounded-lg border transition-all ${
                              darkMode
                                ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-green-500 focus:ring-2 focus:ring-green-500/20"
                                : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-green-500 focus:ring-2 focus:ring-green-500/20"
                            }`
                          )}
                        />
                        <input
                          type="text"
                          value={fieldInput.type}
                          onChange={(e) => setFieldInput({ ...fieldInput, type: e.target.value })}
                          placeholder={textTranslator("fieldTypePlaceholder", "MY_REQUESTS")}
                          className={arClass(
                            `flex-1 px-4 py-2 rounded-lg border transition-all ${
                              darkMode
                                ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-green-500 focus:ring-2 focus:ring-green-500/20"
                                : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-green-500 focus:ring-2 focus:ring-green-500/20"
                            }`
                          )}
                        />
                        </div>
                        <motion.button
                          type="button"
                          onClick={addModelField}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className={arClass(
                            `px-4 py-2 rounded-lg font-semibold transition-all ${
                              darkMode
                                ? "bg-green-600 hover:bg-green-700 text-white"
                                : "bg-green-500 hover:bg-green-600 text-white"
                            }`
                          )}
                        >
                          <Plus className="w-4 h-4" />
                          
                        </motion.button>
                      </div>

                      {/* Fields List */}
                      {modelFields.length > 0 && (
                        <div className={arClass("space-y-2")}>
                          {modelFields.map((field) => (
                            <motion.div
                              key={field.id}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              exit={{ opacity: 0, x: 20 }}
                              className={arClass(
                                `flex items-center justify-between p-3 rounded-lg ${
                                  darkMode
                                    ? "bg-gray-700/50 border border-gray-600"
                                    : "bg-white border border-gray-300"
                                }`
                              )}
                            >
                              <div>
                                <p className={arClass(`font-semibold ${darkMode ? "text-white" : "text-gray-900"}`)}>
                                  {field.name}
                                </p>
                                <p className={arClass(`text-sm ${darkMode ? "text-gray-400" : "text-gray-600"}`)}>
                                  {field.type}
                                </p>
                              </div>
                              <motion.button
                                type="button"
                                onClick={() => removeModelField(field.id)}
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                className="text-red-500 hover:text-red-700 font-bold text-xl"
                              >
                                <X className="w-4 h-4" />
                              </motion.button>
                            </motion.div>
                          ))}
                        </div>
                      )}
                    </div>
                  </motion.div>

                  {/* Relationships */}
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.8 }}
                  >
                    <label className={arClass(`text-sm font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"}`)}>
                      <Text>Relationships</Text>
                    </label>
                    <p className={arClass(`text-xs mt-1 ${darkMode ? "text-gray-400" : "text-gray-600"}`)}>
                      <Text>Link to other entities/tables in your system</Text>
                    </p>
                    <div className={arClass("mt-4 space-y-3")}>
                      {/* Input Field */}
                      <div className={arClass("flex gap-2")}>
                        <input
                          type="text"
                          value={relationshipInput}
                          onChange={(e) => setRelationshipInput(e.target.value)}
                          placeholder={textTranslator("relationshipPlaceholder", "MY_REQUESTS")}
                          className={arClass(
                            `flex-1 px-4 py-2 rounded-lg border transition-all ${
                              darkMode
                                ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-green-500 focus:ring-2 focus:ring-green-500/20"
                                : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-green-500 focus:ring-2 focus:ring-green-500/20"
                            }`
                          )}
                        />
                        <motion.button
                          type="button"
                          onClick={addRelationship}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className={arClass(
                            `px-4 py-2 rounded-lg font-semibold transition-all ${
                              darkMode
                                ? "bg-green-600 hover:bg-green-700 text-white"
                                : "bg-green-500 hover:bg-green-600 text-white"
                            }`
                          )}
                        >
                          <Plus className="w-4 h-4" />
                        </motion.button>
                      </div>

                      {/* Relationships List */}
                      {relationships.length > 0 && (
                        <div className={arClass("space-y-2")}>
                          {relationships.map((rel, index) => (
                            <motion.div
                              key={rel.id}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              exit={{ opacity: 0, x: 20 }}
                              className={arClass(
                                `flex items-center justify-between p-3 rounded-lg ${
                                  darkMode
                                    ? "bg-gray-700/50 border border-gray-600"
                                    : "bg-white border border-gray-300"
                                }`
                              )}
                            >
                              <div>
                                <p className={arClass(`font-semibold ${darkMode ? "text-white" : "text-gray-900"}`)}>
                                  {rel.value}
                                </p>
                                <p className={arClass(`text-xs ${darkMode ? "text-gray-500" : "text-gray-500"}`)}>
                                  relationship_{index + 1}
                                </p>
                              </div>
                              <motion.button
                                type="button"
                                onClick={() => removeRelationship(rel.id)}
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                className="text-red-500 hover:text-red-700 font-bold text-xl"
                              >
                                <X className="w-4 h-4" />
                              </motion.button>
                            </motion.div>
                          ))}
                        </div>
                      )}
                    </div>
                  </motion.div>

                  {/* Mock Data */}
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.85 }}
                  >
                    <label className={arClass(`text-sm font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"}`)}>
                      <Text>Mock Data</Text>
                    </label>
                    <p className={arClass(`text-xs mt-1 ${darkMode ? "text-gray-400" : "text-gray-600"}`)}>
                      <Text>Provide example values to test the model</Text>
                    </p>
                    <div className={arClass("mt-4 space-y-3")}>
                      {/* Input Fields */}
                      <div className={arClass("flex gap-2")}>
                        <div className={arClass("flex gap-2 max-sm:flex-col w-full")}>
                          <input
                          type="text"
                          value={mockDataInput.key}
                          onChange={(e) => setMockDataInput({ ...mockDataInput, key: e.target.value })}
                          placeholder={textTranslator("keyPlaceholder", "MY_REQUESTS")}
                          className={arClass(
                            `flex-1 px-4 py-2 rounded-lg border transition-all ${
                              darkMode
                                ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-green-500 focus:ring-2 focus:ring-green-500/20"
                                : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-green-500 focus:ring-2 focus:ring-green-500/20"
                            }`
                          )}
                        />
                        <input
                          type="text"
                          value={mockDataInput.value}
                          onChange={(e) => setMockDataInput({ ...mockDataInput, value: e.target.value })}
                          placeholder={textTranslator("valuePlaceholder", "MY_REQUESTS")}
                          className={arClass(
                            `flex-1 px-4 py-2 rounded-lg border transition-all ${
                              darkMode
                                ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-green-500 focus:ring-2 focus:ring-green-500/20"
                                : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-green-500 focus:ring-2 focus:ring-green-500/20"
                            }`
                          )}
                        />
                        </div>
                        <motion.button
                          type="button"
                          onClick={addMockData}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className={arClass(
                            `px-4 py-2 rounded-lg font-semibold transition-all ${
                              darkMode
                                ? "bg-green-600 hover:bg-green-700 text-white"
                                : "bg-green-500 hover:bg-green-600 text-white"
                            }`
                          )}
                        >
                          <Plus className="w-4 h-4" />
                        </motion.button>
                      </div>

                      {/* Mock Data List */}
                      {mockData.length > 0 && (
                        <div className={arClass("space-y-2")}>
                          {mockData.map((item) => (
                            <motion.div
                              key={item.id}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              exit={{ opacity: 0, x: 20 }}
                              className={arClass(
                                `flex items-center justify-between p-3 rounded-lg ${
                                  darkMode
                                    ? "bg-gray-700/50 border border-gray-600"
                                    : "bg-white border border-gray-300"
                                }`
                              )}
                            >
                              <div>
                                <p className={arClass(`font-semibold ${darkMode ? "text-white" : "text-gray-900"}`)}>
                                  {item.key}
                                </p>
                                <p className={arClass(`text-sm ${darkMode ? "text-gray-400" : "text-gray-600"}`)}>
                                  {item.value}
                                </p>
                              </div>
                              <motion.button
                                type="button"
                                onClick={() => removeMockData(item.id)}
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                className="text-red-500 hover:text-red-700 font-bold text-xl"
                              >
                                <X className="w-4 h-4" />
                              </motion.button>
                            </motion.div>
                          ))}
                        </div>
                      )}
                    </div>
                  </motion.div>
                </div>
              </motion.div>

              {/* Submit Button */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 }}
                className={arClass("flex gap-4 pt-4")}
              >
                <motion.button
                  type="submit"
                  disabled={loading}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={arClass(
                    `flex-1 flex items-center justify-center gap-2 px-8 py-4 rounded-lg font-bold transition-all ${
                      loading
                        ? "opacity-50 cursor-not-allowed"
                        : darkMode
                        ? "bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white shadow-lg shadow-purple-600/50"
                        : "bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white shadow-lg shadow-purple-500/50"
                    }`
                  )}
                >
                  <Save className="w-5 h-5" />
                  <Text>{loading ? "Creating Request..." : "Create Request"}</Text>
                </motion.button>
              </motion.div>
            </form>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CreateModelRequest;
